import { foundationPathCoordinates } from "building-generation/elements/foundation/pathCoordinates";
import type { Building } from "building-types/building/buildingTypes";
import type { Dimension, DimensionRowOutput, GeneratedDimension } from "building-types/dimensions/dimensionTypes";
import { DimensionRowLevel } from "building-types/dimensions/dimensionTypesEnum";
import { DrawingId } from "building-types/drawings/drawingTypesEnum";
import { LoadType } from "building-types/electrical/electricalTypesEnum";
import type { LoadView } from "building-types/electrical/electricalViewTypes";
import type { SolidFloor } from "building-types/floors/floorTypes";
import { FloorType } from "building-types/floors/floorTypes";
import type { FoundationAssemblyEntity } from "building-types/foundations/foundationAssemblyEntityTypes";
import type { FoundationConditionData } from "building-types/foundations/foundationAssemblyEntityTypes";
import { FoundationAssemblyType } from "building-types/foundations/foundationAssemblyTypesEnum";
import { SlabType, FoundationCondition } from "building-types/foundations/foundationEnums";
import {
  type GeneratedFoundationSubSystem,
  type GeneratedFoundationSuperSystem,
  type HalfEdgeToFoundationPath,
  type FoundationConditionTag,
  type FoundationPlanText,
  type FoundationSection,
  type FoundationStrip,
  type HalfEdgeToFoundationStrip,
  type LinearFoundationSection,
  type Slab,
  TagDirection,
} from "building-types/foundations/foundationTypes";
import {
  autoDimensionFoundationExteriorByOrientationHandleNew,
  autoDimensionFoundationExteriorByRoomHandleMultipleNew,
  autoDimensionFoundationInteriorByOrientationHandleNew,
  autoDimensionFoundationInteriorByRoomHandleMultipleNew,
} from "building-types/handles/autoDimensionHandleTools";
import type { DocEntityHandle, EdgeOrientation, LevelRoomEdge } from "building-types/handles/docEntityHandleTypes";
import type { Prop } from "building-types/props/propTypes";
import type { EdgeData } from "building-types/provenance/halfEdgeDataTypes";
import {
  AutoFoundationExteriorDimensionProvenanceCriteria,
  makeProvenanceCriteria,
} from "building-types/provenance/provenanceCriteriaTools";
import type { ProvenanceCriterion } from "building-types/provenance/provenanceTypes";
import { RoomClass, RoomType } from "building-types/rooms/roomTypesEnum";
import { ExteriorDimensionType, UtilityDimensionType } from "building-types/settings/settingsTypesEnum";
import { WallType } from "building-types/walls/wallEnums";
import type { Table } from "common/data/table";
import { assertIsNotNull, assertIsNotUndefined } from "common/utils/assertOfType";
import { assertUnreachable } from "common/utils/assertUnreachable";
import { DimLineOrientation, getDimLineOrientation, getOrientedDimLine, makeDimension } from "doc/dimensions";
import { foundationSystemSettingsFromSettingsConfig } from "doc/elements/foundation/foundationSettingsTools";
import { FOUNDATION_DIM_OFFSET_MULTIPLIER } from "doc/plans/constants";
import {
  polyLine2DataReplaceIfLengthMismatch,
  edgeDataRemap,
  polygonDataReplaceIfLengthMismatch,
  mapDataToEdgeIndexMap,
} from "doc/provenance/edgeDataTools";
import {
  defaultHalfEdgeData,
  polyLine2EdgeDataNewFromHalfEdgeArray,
  arrayEdgeDataUnion,
  halfEdgeDataNew,
  polyLine2EdgeDataCopy,
  markHalfEdgesAsFromEntityLocation,
  collectDefinedHalfEdges,
} from "doc/provenance/halfEdgeDataTools";
import { planarGraphHalfEdgeStripWithData } from "doc/provenance/planarGraphDataTools";
import { entityContainsBuiltInLabels } from "doc/utils/builtInLabelUtils";
import {
  generatedFoundationExteriorDimensionNew,
  generatedFoundationInteriorDimensionNew,
  getMatchingEdgeOrientation,
  getRoomEdgesFromHalfEdgeData,
} from "doc/utils/dimensionTools";
import {
  isSpanFoundationCondition,
  isDeckFoundationCondition,
  isTransitionFoundationCondition,
  isPorchFoundationCondition,
  isGarageFoundationCondition,
} from "doc/utils/foundationSystemTools";
import { aabb2ByPolyLine2s, aabb2Grow, aabb2Overlaps } from "geom/aabb2";
import type { ClosestPointToPolyLine2 } from "geom/closestPoint";
import { closestPointPolyLine2 } from "geom/closestPoint";
import { polyLine2OverlappingEdgeMapSlow } from "geom/closestPoint";
import { classifyContainmentVec2PolyLine2Fast } from "geom/containment";
import { DIST_EPSILON, Line2CollinearOverlap } from "geom/core";
import { intersectRay2PolyLine2 } from "geom/intersectRay2";
import { joinPolyLine2Sequence, joinPolyLineCollinearSequences } from "geom/join";
import { poleOfInaccessibilityPolyLine2 } from "geom/label";
import { line2Collinear, line2CollinearOverlap, line2Eval, line2Normal } from "geom/line2";
import { offsetPolyLine2, offsetPolyLine2VariableStep } from "geom/offset";
import type { PlanarGraph } from "geom/planarGraph";
import {
  planarGraphAdjacentHalfEdge as ahe,
  planarGraphHalfEdgeStripToPolyLine2,
  planarGraphHalfEdgeTangent,
  planarGraphTracePerimeter,
} from "geom/planarGraph";
import { polygonUnion } from "geom/polygon";
import type { PolygonPlanarGraph } from "geom/polygonPlanarGraph";
import { polygonPlanarGraphNew } from "geom/polygonPlanarGraph";
import {
  polyLine2ByTwoPoints,
  polyLine2Concat,
  polyLine2Copy,
  polyLine2CopySimplify,
  polyLine2Edge,
  polyLine2EdgeCount,
  polyLine2EdgeEval,
  polyLine2EdgeLength,
  polyLine2ExtendEnd,
  polyLine2Length,
  polyLine2MergeCoincidentVertices,
  polyLine2Reverse,
  polyLine2ReverseCopy,
  polyLine2SetCCW,
  polyLine2Simplify,
} from "geom/polyLine2";
import { polygonCopy } from "geom/polyLine2Polygon";
import type { PolyLine2, PolyLine2Polygon } from "geom/polyLine2Types";
import { polyLine2New } from "geom/polyLine2Types";
import { splitPolyLineAtPoints } from "geom/trim";
import type { Vec2 } from "geom/vec2";
import {
  vec2Add,
  vec2AngleBetweenCW,
  vec2Dist,
  vec2DistSquared,
  vec2Dot,
  vec2IsInEdgeBounds,
  vec2Lerp,
  vec2Normalize,
  vec2Scale,
} from "geom/vec2";
import { buildingIsEmpty } from "../building/buildingIsEmpty";
import { isGarageDoorOperationType } from "../elements/doors/doorUtils";
import { defaultFoundationConditionDataNew } from "../elements/foundation/foundationAssemblyTools";
import {
  foundationBrickLedges,
  foundationSlabs,
  foundationStemWalls,
  sectionEndCapPolyLine2,
  sectionGapEnds,
  sectionStartCapPolyLine2,
} from "../elements/foundation/foundationProfiles";
import { isLedgeSectionType, isLinearFoundationSection } from "../elements/foundation/foundationStrip";
import { slabTypeAtRoomClass } from "../elements/foundation/slabUtils";
import { isStemWallInsetSection } from "../elements/foundation/stemWall";
import { levelIsAbove, levelIsAboveOrEqual, levelIsBelow } from "../elements/level/levelTools";
import { makeExteriorDimensionRows, makeInteriorDimensionRows } from "./dimensionRows";
import { makeStairWellWallMassSystem } from "./floorplan";
import type { PropLocationOnPerimeter } from "./propDimensions";
import { makePropLocations, makePropPerimeterIntersections } from "./propDimensions";

const GROW_POLYGON_EPSILON = 0.01;

function findLevelWithRooms(
  startLevelId: string,
  levels: Building["levels"],
  roomsByLevel: Building["roomsByLevel"],
): string | undefined {
  if ((roomsByLevel[startLevelId]?.length ?? 0) > 0) {
    return startLevelId;
  }
  let current = levels[startLevelId];
  while (current?.levelAbove) {
    if ((roomsByLevel[current.levelAbove]?.length ?? 0) > 0) {
      return current.levelAbove;
    }
    current = levels[current.levelAbove];
  }
  return undefined;
}

export function makeFoundationSlabLabels(
  building: Building,
  levelId: string,
  includeExcavationNote: boolean = true,
  includeCrawlspaceNote: boolean = false,
): FoundationPlanText[] {
  const { foundationSuperSystems, levels, floorSystems } = building;
  const labels: FoundationPlanText[] = [];

  for (const foundationId in foundationSuperSystems) {
    const foundationSuperSystem = foundationSuperSystems[foundationId];

    if (!levelIsAboveOrEqual(foundationSuperSystem.levelId, levelId, levels) || !foundationSuperSystem) {
      continue;
    }

    for (const slab of foundationSlabs(foundationSuperSystem)) {
      const pole = poleOfInaccessibilityPolyLine2(slab.planProfile[0]);
      if (!pole) {
        continue;
      }
      const [centerX, centerY] = pole;

      let header = "";
      const detail = "";

      switch (slab.roomClass) {
        case RoomClass.HOUSE:
          header = "HOUSE SLAB";
          break;
        case RoomClass.BASEMENT:
          header = "BASEMENT SLAB";
          break;
        case RoomClass.GARAGE:
          header = "GARAGE SLAB";
          break;
        case RoomClass.PORCH:
          header = "PORCH SLAB";
          break;
        case RoomClass.PATIO:
          header = "PATIO SLAB";
          break;
        case RoomClass.DECK:
        case RoomClass.EXTERIOR:
          break;
        default:
          assertUnreachable(slab.roomClass);
      }

      if (levelIsAbove(foundationSuperSystem.levelId, levelId, levels) && includeExcavationNote) {
        header += `\n (UNEXCAVATED AREA)`;
      }

      labels.push({
        centerX,
        centerY,
        header,
        detail,
      });
    }
  }

  /**
   * We don't have an explicit user defined way to stipulate if a
   * floor actually has a crawlspace beneath it and the autogenerated
   * floor.crawlSpace property is not accurate for all cases, so this value is likely to be
   * unused for the time being
   */
  if (includeCrawlspaceNote) {
    for (const levelId in floorSystems) {
      const floorSystem = floorSystems[levelId];

      if (!levelIsAboveOrEqual(floorSystem.levelId, levelId, levels)) {
        continue;
      }

      for (const roomClassFloors of floorSystem.roomClassFloors) {
        for (const floor of roomClassFloors) {
          if (floor.floorType === FloorType.NONE) {
            continue;
          }
          if (floor.crawlSpace) {
            for (const perimeter of floor.floorPlate.perimeterPolyLine2s) {
              const pole = poleOfInaccessibilityPolyLine2(perimeter);
              if (!pole) {
                continue;
              }

              const [centerX, centerY] = pole;
              const header = "CRAWL SPACE";
              const detail = "";

              labels.push({
                centerX,
                centerY,
                header,
                detail,
              });
            }
          }
        }
      }
    }
  }

  return labels;
}

export function getFoundationProps(building: Pick<Building, "props">, predicate: (Prop) => boolean): Prop[] {
  const { props } = building;
  const foundationProps: Prop[] = [];

  Object.values(props)
    .filter(predicate)
    .forEach((p) => {
      foundationProps.push(p);
    });

  return foundationProps;
}

export function makeFoundationPropLinework(propLocations: PropLocationOnPerimeter[]) {
  const linework: PolyLine2[] = [];
  const dashedLinework: PolyLine2[] = [];

  // add the plumbing walls to the plan and obstacles array
  // add the prop bounds to the obstacles array
  propLocations.sort((a, b) => {
    const { room: aRoom } = a.prop;
    const { room: bRoom } = b.prop;
    const roomCompare = (aRoom?.id ?? "").localeCompare(bRoom?.id ?? "");
    if (roomCompare !== 0) {
      return roomCompare;
    }
    return a.propRoomInfo.roomEdgeIndex - b.propRoomInfo.roomEdgeIndex;
  });

  for (let propLocationIndex = 0; propLocationIndex < propLocations.length; propLocationIndex++) {
    const propLocation = propLocations[propLocationIndex];
    const { prop, propRoomInfo, isFreeProp, isDrawOnly, isEdgeWall } = propLocation;
    const { room } = prop;
    const { roomEdgeIndex } = propRoomInfo;

    // don't draw host walls for free props or draw-only props
    if (isFreeProp || isDrawOnly || isEdgeWall) {
      continue;
    }

    if (
      propLocationIndex === 0 ||
      propLocations[propLocationIndex - 1]?.prop?.room?.id !== room?.id ||
      propLocations[propLocationIndex - 1].propRoomInfo.roomEdgeIndex !== roomEdgeIndex
    ) {
      linework.push(propLocation.propRoomInfo.roomEdgeWallBaseLine);
      dashedLinework.push(propLocation.propRoomInfo.roomEdgeWallCenterLine);
    }
  }
  return { linework, dashedLinework };
}

export function makeFoundationPerimeters(
  building: Pick<
    Building,
    | "settings"
    | "foundations"
    | "foundationSuperSystems"
    | "foundationPaths"
    | "foundationHorizontalAssembliesPaths"
    | "levels"
    | "floorSystems"
    | "planarGraphs"
    | "roomsByLevel"
  >,
  levelId: string,
) {
  const {
    settings: { planset: planSettings },
    foundations,
    foundationSuperSystems,
    foundationPaths,
    levels,
    floorSystems,
  } = building;

  const exteriorPerimeters: PolyLine2[] = [];
  const exteriorPerimeterDetails: PolyLine2[] = [];
  const interiorPerimeters: PolyLine2[] = [];

  const interiorPerimeterHalfEdges: EdgeData[][][] = [];
  const exteriorPerimeterHalfEdges: EdgeData[][][] = [];
  const exteriorPerimeterDetailsHalfEdges: EdgeData[][][] = [];

  function remapHalfEdgeDataLevel(edgeData: EdgeData[][]): EdgeData[][] {
    if (!edgeData.length) {
      return edgeData;
    }
    const sampleLevelId = edgeData[0]?.[0]?.levelId;
    if (!sampleLevelId) {
      return edgeData;
    }
    const targetLevel = findLevelWithRooms(sampleLevelId, levels, building.roomsByLevel);
    if (!targetLevel || targetLevel === sampleLevelId) {
      return edgeData;
    }
    return edgeData.map((edges) => edges.map((ed) => (ed ? { ...ed, levelId: targetLevel } : ed)));
  }

  function addSlabPerimeters(slab: Slab, foundationSubSystem: GeneratedFoundationSubSystem) {
    const interiorProfile = polyLine2Copy(slab.planProfile[0]);
    const exteriorProfile = polyLine2Copy(slab.exteriorProfile[0]);
    const predicate = (section: LinearFoundationSection) => {
      const sectionOverlaps = section.size.top > slab.size.top;
      const sectionHasOpenings = !!section.size.openings.length;
      return (
        (sectionOverlaps && sectionHasOpenings) || (planSettings.dimensionBrickLedge && isLedgeSectionType(section))
      );
    };
    switch (slab.roomClass) {
      case RoomClass.HOUSE:
      case RoomClass.BASEMENT:
      case RoomClass.GARAGE: {
        exteriorPerimeters.push(exteriorProfile);
        interiorPerimeters.push(interiorProfile);
        exteriorPerimeterHalfEdges.push(remapHalfEdgeDataLevel(slab.exteriorProfileHalfEdges[0]));
        interiorPerimeterHalfEdges.push(remapHalfEdgeDataLevel(slab.planProfileHalfEdges[0]));

        const { slabProfile, slabProfileHalfEdges: slabAtHouseBasementGarageHalfEdges } =
          foundationSlabSplitDimensionProfileAtSectionsGaps(foundationSubSystem, slab, exteriorProfile, predicate);

        exteriorPerimeterDetails.push(slabProfile);
        exteriorPerimeterDetailsHalfEdges.push(remapHalfEdgeDataLevel([...slabAtHouseBasementGarageHalfEdges]));
        break;
      }
      case RoomClass.PORCH: {
        const { slabProfile, slabProfileHalfEdges: slabAtPorchHalfEdges } =
          foundationSlabSplitDimensionProfileAtSectionsGaps(foundationSubSystem, slab, exteriorProfile, predicate);
        exteriorPerimeterDetails.push(slabProfile);
        exteriorPerimeterDetailsHalfEdges.push(remapHalfEdgeDataLevel(slabAtPorchHalfEdges));
        break;
      }
      case RoomClass.PATIO: {
        exteriorPerimeterDetails.push(exteriorProfile);
        exteriorPerimeterDetailsHalfEdges.push(
          remapHalfEdgeDataLevel(
            polyLine2DataReplaceIfLengthMismatch(
              exteriorProfile,
              slab.exteriorProfileHalfEdges[0],
              defaultHalfEdgeData(foundationSubSystem.planarGraphId),
            ),
          ),
        );
        break;
      }
      case RoomClass.DECK:
      case RoomClass.EXTERIOR:
        break;
      default:
        assertUnreachable(slab.roomClass);
    }
  }

  function traceFloorplateSubSystemPerimeters(
    floor: SolidFloor,
    levelId: string,
    foundationPaths: HalfEdgeToFoundationPath,
  ) {
    const perimeters: PolyLine2[] = [];
    const { floorPlate } = floor;
    const perimeterHalfEdges: EdgeData[][][] = [];
    for (let i = 0; i < floorPlate.perimeters.faceHalfEdges.length; i++) {
      const loop = floorPlate.perimeters.faceHalfEdges[i];

      /**
       * If there's a foundation system, and all paths in the floorplate loop are void, then don't include the perimeter
       */
      if (foundationPaths) {
        let allFoundationPathsVoid = true;
        for (const he of loop) {
          const foundationPath = foundationPaths[he];
          const assemblyType = foundationPath?.assembly?.assemblyType;
          if (assemblyType === undefined) {
            /**
             * If foundationPath, assembly, or assemblyType is undefined, then let's not get hasty
             * and try to figure out if all paths are void
             */
            allFoundationPathsVoid = false;
            break;
          }
          if (assemblyType !== FoundationAssemblyType.VOID) {
            allFoundationPathsVoid = false;
          }
        }

        if (allFoundationPathsVoid) {
          return { perimeters, perimeterHalfEdges };
        }
      }

      const planarGraph = building.planarGraphs[levelId].planarGraph;
      const perimeter = planarGraphHalfEdgeStripToPolyLine2(planarGraph, loop);
      const offsets = loop.map((he) => (foundationPaths ? foundationPaths[he]?.subSystemOffset ?? 0 : 0));
      const { pl: offsetPerimeter, edgeMapping } = offsetPolyLine2VariableStep(perimeter, offsets, true);
      const offsetPerimeterHalfEdges = mapDataToEdgeIndexMap(edgeMapping, loop).map((he) => [he]);
      const offsetPerimeterSimplified = polyLine2CopySimplify(offsetPerimeter);
      perimeters.push(offsetPerimeterSimplified);

      perimeterHalfEdges.push(
        edgeDataRemap(
          polyLine2OverlappingEdgeMapSlow(offsetPerimeter, offsetPerimeterSimplified),
          polyLine2EdgeDataNewFromHalfEdgeArray(levelId, offsetPerimeterHalfEdges),
          arrayEdgeDataUnion,
          polyLine2EdgeCount(offsetPerimeterSimplified),
          defaultHalfEdgeData(levelId),
        ),
      );
    }

    return { perimeters, perimeterHalfEdges };
  }

  function addFloorplateSubSystemPerimeters(
    floor: SolidFloor,
    levelId: string,
    foundationPaths: HalfEdgeToFoundationPath,
  ) {
    switch (floor.roomClass) {
      case RoomClass.HOUSE:
      case RoomClass.BASEMENT:
      case RoomClass.GARAGE: {
        const { perimeters, perimeterHalfEdges } = traceFloorplateSubSystemPerimeters(floor, levelId, foundationPaths);

        exteriorPerimeters.push(...perimeters);
        interiorPerimeters.push(...perimeters);

        exteriorPerimeterHalfEdges.push(...perimeterHalfEdges);
        interiorPerimeterHalfEdges.push(...perimeterHalfEdges);
        break;
      }
      case RoomClass.PORCH:
      case RoomClass.DECK:
      case RoomClass.PATIO: {
        const { perimeters, perimeterHalfEdges } = traceFloorplateSubSystemPerimeters(floor, levelId, foundationPaths);

        exteriorPerimeters.push(...perimeters);

        exteriorPerimeterHalfEdges.push(...perimeterHalfEdges);
        break;
      }
      case RoomClass.EXTERIOR:
        break;
      default:
        assertUnreachable(floor.roomClass);
    }
  }

  for (const levelId in floorSystems) {
    const floorSystem = floorSystems[levelId];
    const levelFoundationPaths = foundationPaths[levelId];

    if (!levelIsAboveOrEqual(floorSystem.levelId, levelId, levels)) {
      continue;
    }

    for (const roomClassFloors of floorSystem.roomClassFloors) {
      for (const floor of roomClassFloors) {
        if (floor.floorType === FloorType.SOLID && floor.crawlSpace) {
          addFloorplateSubSystemPerimeters(floor, levelId, levelFoundationPaths);
        }
      }
    }
  }

  for (const id in levels) {
    if (!levelIsAboveOrEqual(id, levelId, levels)) {
      continue;
    }
    // Find foundation super-system for this level
    let foundationSuperSystem: GeneratedFoundationSuperSystem | undefined;
    for (const foundationSuperSystemId in foundationSuperSystems) {
      const foundationSuperSystemCandidate = foundationSuperSystems[foundationSuperSystemId];
      if (foundationSuperSystemCandidate.levelId === id) {
        foundationSuperSystem = foundationSuperSystemCandidate;
        break;
      }
    }
    if (!foundationSuperSystem) {
      continue;
    }
    // Find foundation sub-system for this level
    let foundationSubSystem: GeneratedFoundationSubSystem | undefined;
    for (const foundationId in foundations) {
      const foundation = foundations[foundationId];
      if (foundation.levelId === id) {
        foundationSubSystem = foundation;
        break;
      }
    }
    if (!foundationSubSystem) {
      continue;
    }
    for (const slab of foundationSlabs(foundationSuperSystem)) {
      if (slab.isSlabDepression) {
        continue;
      }
      addSlabPerimeters(slab, foundationSubSystem);
    }
  }

  return {
    exteriorPerimeters,
    exteriorPerimeterDetails,
    interiorPerimeters,
    exteriorPerimeterHalfEdges: polygonDataReplaceIfLengthMismatch(
      exteriorPerimeters,
      exteriorPerimeterHalfEdges,
      defaultHalfEdgeData(undefined),
    ),
    interiorPerimeterHalfEdges,
    exteriorPerimeterDetailsHalfEdges,
  };
}

export async function makeFoundationPlanDimensionsByLevel(
  building: Pick<
    Building,
    | "settings"
    | "foundations"
    | "foundationSuperSystems"
    | "foundationPaths"
    | "foundationHorizontalAssembliesPaths"
    | "levels"
    | "electricalSystemView"
    | "planarGraphs"
    | "walls"
    | "floorSystems"
    | "mirror"
    | "roomsByLevel"
    | "rooms"
    | "enabledFeatures"
  >,
  levelId: string,
  conditionTags: FoundationConditionTag[],
  propLocations: PropLocationOnPerimeter[],
): Promise<{ interiorDimensions: GeneratedDimension[]; exteriorDimensions: GeneratedDimension[] }> {
  const {
    settings: { planset: planSettings },
    electricalSystemView,
    foundations,
    foundationSuperSystems,
    levels,
  } = building;

  const halfEdgeLevelId = findLevelWithRooms(levelId, levels, building.roomsByLevel) ?? levelId;

  const { loads } = electricalSystemView[levelId];

  const exteriorPerimeterProjections: PolyLine2[] = [];
  const exteriorPerimeterProjectionHalfEdges: EdgeData[][][] = [];
  const exteriorPerimeterProjectionsNonObstructing: PolyLine2[] = []; // these are non obstructing to dimensions
  const exteriorObstacles: PolyLine2[] = [];
  const exteriorBounds: PolyLine2[] = [];

  const interiorEdges: PolyLine2[] = [];
  const interiorEdgeHalfEdges: EdgeData[][][] = [];
  const interiorObstacles: PolyLine2[] = [];

  const dimPoints: Vec2[] = [];

  const {
    exteriorPerimeters,
    exteriorPerimeterHalfEdges,
    exteriorPerimeterDetails,
    exteriorPerimeterDetailsHalfEdges,
    interiorPerimeters,
    interiorPerimeterHalfEdges,
  } = makeFoundationPerimeters(building, levelId);

  function addSlabDepressions(slab: Slab) {
    const { planProfile, planProfileHalfEdges } = slab; //planProfileHalfEdges TODO
    for (let i = 1; i < planProfile.length; i++) {
      interiorObstacles.push(polyLine2ReverseCopy(planProfile[i]));
      for (let e = 0, l = polyLine2EdgeCount(planProfile[i]); e < l; e++) {
        interiorEdges.push(getOrientedDimLine(polyLine2Edge(planProfile[i], e)));
        interiorEdgeHalfEdges.push(polyLine2EdgeDataCopy(planProfileHalfEdges[i]));
      }
      for (let x = 0; x < planProfile[i].length; x += 2) {
        dimPoints.push([planProfile[i][x], planProfile[i][x + 1]]);
      }
    }
  }

  function addSectionGaps(section: FoundationSection) {
    const ends = sectionGapEnds(section);
    for (let i = 0; i < ends.length; i++) {
      if (i % 2) {
        interiorEdges.push(ends[i]);
        interiorEdgeHalfEdges.push(
          polyLine2EdgeDataNewFromHalfEdgeArray(halfEdgeLevelId, [[section?.path?.halfEdgeIndex]]),
        );
      } else {
        interiorObstacles.push(ends[i]);
      }
    }
  }

  function addSectionEndCaps(section: LinearFoundationSection) {
    if (section.endCap.isFreeEnd) {
      exteriorPerimeterProjections.push(sectionEndCapPolyLine2(section));
      exteriorPerimeterProjectionHalfEdges.push([[halfEdgeDataNew(halfEdgeLevelId, section.path.halfEdgeIndex)]]);

      interiorEdges.push(sectionEndCapPolyLine2(section));
      interiorEdgeHalfEdges.push(
        polyLine2EdgeDataNewFromHalfEdgeArray(halfEdgeLevelId, [[section?.path?.halfEdgeIndex]]),
      );
    }
    if (section.startCap.isFreeEnd) {
      exteriorPerimeterProjections.push(sectionStartCapPolyLine2(section));
      exteriorPerimeterProjectionHalfEdges.push([[halfEdgeDataNew(halfEdgeLevelId, section.path.halfEdgeIndex)]]);

      interiorEdges.push(sectionStartCapPolyLine2(section));
      interiorEdgeHalfEdges.push(
        polyLine2EdgeDataNewFromHalfEdgeArray(halfEdgeLevelId, [[section?.path?.halfEdgeIndex]]),
      );
    }
  }

  for (const foundationId in foundations) {
    const foundationSystem = foundations[foundationId];
    if (!levelIsAboveOrEqual(foundationSystem.levelId, levelId, levels)) {
      continue;
    }

    for (const stemWall of foundationStemWalls(foundationSystem)) {
      if (!isStemWallInsetSection(stemWall)) {
        addSectionGaps(stemWall);
      }
    }

    for (const brickLedge of foundationBrickLedges(foundationSystem)) {
      if (!isLinearFoundationSection(brickLedge)) {
        continue;
      }
      exteriorBounds.push(...brickLedge.planProfiles); // add bounds for exterior consistency
      if (planSettings.dimensionBrickLedge && !isStemWallInsetSection(brickLedge)) {
        addSectionGaps(brickLedge);
        addSectionEndCaps(brickLedge);
      }
    }
  }

  for (const foundationSuperSystemId in foundationSuperSystems) {
    const foundationSuperSystem = foundationSuperSystems[foundationSuperSystemId];
    if (!levelIsAboveOrEqual(foundationSuperSystem.levelId, levelId, levels)) {
      continue;
    }

    for (const slab of foundationSlabs(foundationSuperSystem)) {
      if (slab.isSlabDepression) {
        continue;
      }
      addSlabDepressions(slab);
    }
  }

  // dimensions on interior footings
  conditionTags.filter(foundationConditionPredicate(building)).forEach((c) => {
    const stripPath = c.strips[0]?.path;
    for (const pl of c.lines) {
      const dimLine = getOrientedDimLine(pl);
      const dimOrientation = getDimLineOrientation(dimLine);
      const t = dimOrientation === DimLineOrientation.TOP ? 0.9 : 0.1;

      const [sx, sy, ex, ey] = dimLine;
      const [dimStartX, dimStartY] = line2Eval(sx, sy, ex, ey, t, true);
      const [dimDirX, dimDirY] = line2Normal(sx, sy, ex, ey);

      for (let i = 0; i < exteriorPerimeters.length; i++) {
        const slabProfile = exteriorPerimeters[i];
        const slabProfileHalfEdges = exteriorPerimeterHalfEdges[i];
        if (classifyContainmentVec2PolyLine2Fast(dimStartX, dimStartY, slabProfile)) {
          const ints = intersectRay2PolyLine2(slabProfile, dimStartX, dimStartY, dimDirX, dimDirY);

          if (ints.length !== 0) {
            ints.sort((a, b) => Math.abs(a.t) - Math.abs(b.t));

            const ept = ints[0];
            const dimEdge = polyLine2ByTwoPoints(dimStartX, dimStartY, ept.x, ept.y);
            interiorEdges.push(polyLine2Reverse(getOrientedDimLine(dimEdge)));
            const intersectedEdgeHalfEdges = slabProfileHalfEdges[ept.edgeIndex];
            const hasValidHalfEdge = intersectedEdgeHalfEdges?.some((ed) => ed?.halfEdge !== undefined);
            if (hasValidHalfEdge) {
              interiorEdgeHalfEdges.push([intersectedEdgeHalfEdges]);
            } else if (stripPath) {
              interiorEdgeHalfEdges.push([[halfEdgeDataNew(halfEdgeLevelId, stripPath.halfEdgeIndex)]]);
            } else {
              interiorEdgeHalfEdges.push([]);
            }
          }

          break;
        }
      }
    }
  });

  // dimensions on electrical loads
  loads.filter(foundationElectricalLoadPredicate(building, levelId)).forEach((l) => {
    dimPoints.push([l.positionX, l.positionY]);
  });

  /**
   * exterior dims
   */
  const exteriorPerimeterGraph = polygonPlanarGraphNew(exteriorPerimeters);
  const exteriorPerimeterLoops = planarGraphTracePerimeter(exteriorPerimeterGraph.planarGraph);

  const { perimeterPolygons: exteriorPerimeterPolygons, polygonData: exteriorPerimeterPolygonData } =
    planarGraphHalfEdgeStripWithData(exteriorPerimeterGraph, exteriorPerimeterLoops, exteriorPerimeterHalfEdges);

  const exteriorPerimeterDetailHalfEdgesCombined = [
    ...exteriorPerimeterHalfEdges,
    ...exteriorPerimeterDetailsHalfEdges,
  ];

  const exteriorPerimeterDetailGraph = polygonPlanarGraphNew([
    ...polygonCopy(exteriorPerimeters),
    ...polygonCopy(exteriorPerimeterDetails),
  ]);

  const exteriorPerimeterDetailLoops = planarGraphTracePerimeter(exteriorPerimeterDetailGraph.planarGraph);
  const { perimeterPolygons: exteriorPerimeterDetailPolygons, polygonData: exteriorPerimeterDetailPolygonData } =
    planarGraphHalfEdgeStripWithData(
      exteriorPerimeterDetailGraph,
      exteriorPerimeterDetailLoops,
      exteriorPerimeterDetailHalfEdgesCombined,
    );

  // make the slab intersection sets
  const { interiorIntersectionsByPerimeter, exteriorIntersectionsByPerimeter } = makePropPerimeterIntersections(
    propLocations,
    dimPoints,
    exteriorPerimeters,
  );

  for (const slabIndex in exteriorIntersectionsByPerimeter) {
    for (const slabEdgeIndex in exteriorIntersectionsByPerimeter[slabIndex]) {
      const edge = polyLine2Copy(polyLine2Edge(exteriorPerimeters[slabIndex], +slabEdgeIndex));
      const halfEdges = exteriorPerimeterHalfEdges[slabIndex][slabEdgeIndex];
      for (const intersection of exteriorIntersectionsByPerimeter[slabIndex][slabEdgeIndex]) {
        const ept = polyLine2EdgeEval(edge, 0, intersection.perimeterEdgeIntersection.t, true);
        const dimEdge1 = polyLine2ByTwoPoints(edge[0], edge[1], ept[0], ept[1]);
        const dimEdge2 = polyLine2ByTwoPoints(ept[0], ept[1], edge[2], edge[3]);

        // some exterior slab edge intersections are not perimeter intersections,
        // and need to be added to the interior edge dimensions instead
        let isPerimeter = false;
        for (const perim of exteriorPerimeterPolygons) {
          const cpt = closestPointPolyLine2(perim, ept[0], ept[1]);
          if (cpt.dist <= intersection.perimeterEdgeIntersection.dist) {
            isPerimeter = true;
            break;
          }
        }

        if (isPerimeter) {
          exteriorPerimeterProjections.push(dimEdge1);
          exteriorPerimeterProjectionHalfEdges.push([markHalfEdgesAsFromEntityLocation(halfEdges)]);
        } else {
          interiorEdges.push(offsetPolyLine2(polyLine2Reverse(dimEdge1), intersection.perimeterEdgeIntersection.dist));
          interiorEdgeHalfEdges.push([markHalfEdgesAsFromEntityLocation(halfEdges)]);
          interiorEdges.push(offsetPolyLine2(polyLine2Reverse(dimEdge2), intersection.perimeterEdgeIntersection.dist));
          interiorEdgeHalfEdges.push([markHalfEdgesAsFromEntityLocation(halfEdges)]);
        }
      }
    }
  }

  const exteriorDimensions: DimensionRowOutput[] = [];
  const interiorDimensions: DimensionRowOutput[] = [];
  const rowConfig = building.settings.planset.exteriorDimensionType;
  const mergeRows3And4 = rowConfig === ExteriorDimensionType.THREE_ROW;

  makeExteriorDimensionRows({
    output: exteriorDimensions,
    input: [
      { level: DimensionRowLevel.BOUNDS, pls: exteriorBounds },
      {
        level: DimensionRowLevel.ROW_2,
        pls: exteriorPerimeterPolygons,
        projectOnAxis: true,
        halfEdges: exteriorPerimeterPolygonData,
      },
      {
        level: DimensionRowLevel.ROW_3,
        pls: exteriorPerimeterDetailPolygons,
        halfEdges: exteriorPerimeterDetailPolygonData,
      },
      {
        level: DimensionRowLevel.ROW_4,
        pls: exteriorPerimeterProjections,
        halfEdges: exteriorPerimeterProjectionHalfEdges,
      },
      { level: DimensionRowLevel.ROW_4, pls: exteriorPerimeterProjectionsNonObstructing, isNonObstructing: true },
      { level: DimensionRowLevel.OBSTACLE, pls: exteriorObstacles },
    ],
    mergeRows3And4,
    baseLineOffsetMultiplier: FOUNDATION_DIM_OFFSET_MULTIPLIER,
    halfEdgePropagationConfig: { [DimensionRowLevel.ROW_3]: { [DimensionRowLevel.ROW_3]: true } },
  });

  /**
   * interior dims
   */
  // add the interior intersections to the dims
  for (const slabIndex in interiorIntersectionsByPerimeter) {
    for (const slabEdgeIndex in interiorIntersectionsByPerimeter[slabIndex]) {
      const halfEdges = exteriorPerimeterHalfEdges[slabIndex][slabEdgeIndex];
      for (const intersection of interiorIntersectionsByPerimeter[slabIndex][slabEdgeIndex]) {
        const spt = intersection.origin;
        const ept = intersection.perimeterEdgeIntersection;
        const dimEdge = polyLine2ByTwoPoints(spt[0], spt[1], ept.x, ept.y);
        interiorEdges.push(polyLine2Reverse(getOrientedDimLine(dimEdge)));
        interiorEdgeHalfEdges.push([markHalfEdgesAsFromEntityLocation(halfEdges)]);
      }
    }
  }

  if (levels[levelId].floorIndex < 0) {
    const walls = makeStairWellWallMassSystem(building, levelId).walls;
    for (const wallId in walls) {
      const wall = walls[wallId];
      if (wall.wallType === WallType.SOLID_MASS) {
        const { leftRoom, rightRoom, halfEdgeIndex } = wall.path;
        const { leftEdge, rightEdge } = wall;
        const lStair = leftRoom?.type === RoomType.STAIRS;
        const rStair = rightRoom?.type === RoomType.STAIRS;
        if (lStair) {
          interiorEdges.push(polyLine2Reverse(getOrientedDimLine(polyLine2Copy(rightEdge))));
          interiorEdgeHalfEdges.push([[halfEdgeDataNew(halfEdgeLevelId, halfEdgeIndex)]]);
        }
        if (rStair) {
          interiorEdges.push(polyLine2Reverse(getOrientedDimLine(polyLine2Copy(leftEdge))));
          interiorEdgeHalfEdges.push([[halfEdgeDataNew(halfEdgeLevelId, halfEdgeIndex)]]);
        }
      }
    }
  }

  makeInteriorDimensionRows({
    dimensions: interiorDimensions,
    perimeters: interiorPerimeters,
    interiorEdges: interiorEdges,
    obstacles: interiorObstacles,
    baseLineOffsetMultiplier: FOUNDATION_DIM_OFFSET_MULTIPLIER,
    perimeterHalfEdges: interiorPerimeterHalfEdges,
    interiorEdgeHalfEdges,
  });

  return {
    interiorDimensions: generatedFoundationInteriorDimensionHydrateDocEntityHandle(
      interiorDimensions,
      building,
      levelId,
    ),
    exteriorDimensions: generatedFoundationExteriorDimensionHydrateDocEntityHandle(
      exteriorDimensions,
      building,
      levelId,
    ),
  };
}

export async function makeFoundationUtilityPlanDimensions(building: Building, levelId: string): Promise<Dimension[]> {
  if (buildingIsEmpty(building)) {
    return [];
  }

  const { electricalSystemView } = building;
  const { loads } = electricalSystemView[levelId];

  const exteriorDimensions: Dimension[] = [];
  const interiorDimensions: Dimension[] = [];

  const { slabProfiles, filterSlabEdge } = getSlabProfilesAndFilterForUtilityPlanContent(building, levelId);

  const dimProps: Prop[] = getFoundationProps(building, foundationUtilityPropPredicate(building, levelId));
  const dimPoints: Vec2[] = [];

  // dimensions on electrical loads
  loads.filter(foundationUtilityElectricalLoadPredicate(building, levelId)).forEach((l) => {
    dimPoints.push([l.positionX, l.positionY]);
  });

  // make the slab intersection sets
  const propLocations = await makePropLocations(building, dimProps, slabProfiles);
  const { interiorIntersectionsByPerimeter, exteriorIntersectionsByPerimeter } = makePropPerimeterIntersections(
    propLocations,
    dimPoints,
    slabProfiles,
    true,
    filterSlabEdge,
  );

  //add the exterior intersections to the dims
  for (const slabIndex in exteriorIntersectionsByPerimeter) {
    for (const slabEdgeIndex in exteriorIntersectionsByPerimeter[slabIndex]) {
      const edge = polyLine2Copy(polyLine2Edge(slabProfiles[slabIndex], +slabEdgeIndex));
      const offset = 10;

      const intersections = exteriorIntersectionsByPerimeter[slabIndex][slabEdgeIndex];
      // intersections sorted by position on slab edge
      const ts = [...intersections].sort((a, b) => a.perimeterEdgeIntersection.t - b.perimeterEdgeIntersection.t);

      let dimEdgeIsRight = false;

      let count = 1;
      let lT = 0;

      for (let i = 0; i < ts.length; i++) {
        const intersection = ts[i];
        const t = intersection.perimeterEdgeIntersection.t;
        const dist = intersection.perimeterEdgeIntersection.dist;

        // don't add duplicate dims
        if (lT && Math.abs(lT - t) < DIST_EPSILON) {
          continue;
        }

        const ept = polyLine2EdgeEval(edge, 0, t, true);

        let dimEdge: PolyLine2;

        // passed the halfway mark, so the counter needs to be reversed
        // all subsequent dims will go to the right edge
        if (lT <= 0.5 && t > 0.5) {
          count = intersections.length - i;
          dimEdgeIsRight = true;
        }

        if (dimEdgeIsRight) {
          dimEdge = polyLine2ByTwoPoints(ept[0], ept[1], edge[2], edge[3]);
        } else {
          dimEdge = polyLine2ByTwoPoints(edge[0], edge[1], ept[0], ept[1]);
        }

        offsetPolyLine2(polyLine2Reverse(dimEdge), dist);

        const startExt = dimEdgeIsRight ? 0 : dist;
        const endExt = dimEdgeIsRight ? dist : 0;

        makeDimension(exteriorDimensions, {
          pl: dimEdge,
          baseLineOffset: offset * count,
          startExt,
          endExt,
          dimExt: 0,
          witnessLineGap: 0,
        });

        if (dimEdgeIsRight) {
          count--;
        } else {
          count++;
        }

        lT = t;
      }
    }
  }

  const { mirror } = building;
  const negate = !mirror;
  const offset = 10;

  // add the interior intersections to the dims
  for (const slabIndex in interiorIntersectionsByPerimeter) {
    for (const slabEdgeIndex in interiorIntersectionsByPerimeter[slabIndex]) {
      const originalEdge = polyLine2Copy(polyLine2Edge(slabProfiles[slabIndex], +slabEdgeIndex));
      const edge = mirror ? polyLine2Reverse(originalEdge) : originalEdge;
      const edgeLen = polyLine2EdgeLength(edge, 0);

      const [normalX, normalY] = line2Normal(edge[0], edge[1], edge[2], edge[3], negate);

      const originalIntersections = interiorIntersectionsByPerimeter[slabIndex][slabEdgeIndex];

      const intersections = mirror
        ? originalIntersections.map((x) => {
            return {
              ...x,
              perimeterEdgeIntersection: { ...x.perimeterEdgeIntersection, t: 1 - x.perimeterEdgeIntersection.t },
            };
          })
        : originalIntersections;

      // intersections sorted by position on slab edge
      const ts = [...intersections].sort((a, b) => a.perimeterEdgeIntersection.t - b.perimeterEdgeIntersection.t);
      // intersections sorted by distance to slab edge
      const ds = [...intersections].sort((a, b) => a.perimeterEdgeIntersection.dist - b.perimeterEdgeIntersection.dist);

      let index = 0;
      let count = 1;

      let leptX: number | undefined;
      let leptY: number | undefined;

      // iterate from the closest intersection to the slab edge,
      // with intersection T values sorted increasingly on the edge
      for (let i = 0; i < ds.length; i++) {
        const intersection = ds[i];
        const dist = intersection.perimeterEdgeIntersection.dist;
        const tt = intersection.perimeterEdgeIntersection.t;

        let t = ts[index].perimeterEdgeIntersection.t;
        // the current t may be behind this t,
        // so we need to increase the index for t until we are in front
        while (tt > t && index < intersections.length - 1) {
          index++;
          t = ts[index].perimeterEdgeIntersection.t;
        }

        const origin = intersection.origin;

        // don't add duplicate dims
        // wondering if we should also make sure the last and current intersections are not the same??
        const lastDist = ds[i - 1]?.perimeterEdgeIntersection.dist;
        if (lastDist && Math.abs(dist - lastDist) < DIST_EPSILON) {
          continue;
        }

        const [dirX, dirY] = vec2Scale(normalX, normalY, dist);
        const [eptX, eptY] = polyLine2EdgeEval(edge, 0, t, true);

        // if this dimLine is farther away than the current counter dimLine,
        // we can reset the counter
        const lastExt = leptX !== undefined && leptY !== undefined ? vec2Dist(leptX, leptY, eptX, eptY) : 0;

        if (count > 1 && lastExt > offset * (count - 1)) {
          count = 1;
        }

        const [sptX, sptY] = vec2Add(eptX, eptY, dirX, dirY);
        const ext = vec2Dist(origin[0], origin[1], sptX, sptY);

        const dimEdge = polyLine2ByTwoPoints(sptX, sptY, eptX, eptY);

        let baseLineOffset = mirror ? -offset * count : offset * count;
        if (count > 1) {
          baseLineOffset -= lastExt;
        }

        const remainder = edgeLen * (1 - t);
        let endExt = 0;
        if (baseLineOffset < remainder) {
          endExt = -baseLineOffset;
        }

        makeDimension(interiorDimensions, {
          pl: dimEdge,
          baseLineOffset,
          witnessLineGap: 0,
          dimExt: 2,
          startExt: ext,
          endExt,
        });

        count++;

        leptX = eptX;
        leptY = eptY;
      }
    }
  }

  return [...exteriorDimensions, ...interiorDimensions];
}

export async function makeFoundationUtilityPlanLinework(
  building: Building,
  levelId: string,
): Promise<{
  linework: PolyLine2[];
  dashedLinework: PolyLine2[];
}> {
  const linework: PolyLine2[] = [];
  const dashedLinework: PolyLine2[] = [];

  if (buildingIsEmpty(building)) {
    return { dashedLinework, linework };
  }

  const { slabProfiles } = getSlabProfilesAndFilterForUtilityPlanContent(building, levelId);

  const { electricalSystemView } = building;
  const { loads } = electricalSystemView[levelId];

  const dimProps: Prop[] = getFoundationProps(building, foundationUtilityPropPredicate(building, levelId));
  const dimPoints: Vec2[] = [];

  // dimensions on electrical loads
  loads.filter(foundationUtilityElectricalLoadPredicate(building, levelId)).forEach((l) => {
    dimPoints.push([l.positionX, l.positionY]);
  });

  const propLocations = await makePropLocations(building, dimProps, slabProfiles);
  const propLocationRoomEdgeIndices: { [roomId: string]: { [edgeIndex: number]: true } } = {};
  // add the plumbing walls to the plan and obstacles array
  // add the prop bounds to the obstacles array
  for (const propLocation of propLocations) {
    const { prop, propRoomInfo, isFreeProp, isDrawOnly, isEdgeWall } = propLocation;
    const { room } = prop;
    const { roomEdgeIndex, roomEdgeWallThickness } = propRoomInfo;

    // don't draw host walls for free props or draw-only props
    if (isFreeProp || isDrawOnly || isEdgeWall) {
      continue;
    }

    assertIsNotNull(room);
    // only want to draw the wall once
    if (!propLocationRoomEdgeIndices[room.id]) {
      propLocationRoomEdgeIndices[room.id] = {};
    }

    if (!propLocationRoomEdgeIndices[room.id][roomEdgeIndex]) {
      propLocationRoomEdgeIndices[room.id][roomEdgeIndex] = true;

      const pl = propLocation.propRoomInfo.roomEdgeWallBaseLine;
      const plb = polyLine2Reverse(offsetPolyLine2(pl, roomEdgeWallThickness));
      const section = polyLine2Concat(pl, plb, true);

      linework.push(propLocation.propRoomInfo.roomEdgeWallBaseLine);
      dashedLinework.push(section);
    }
  }

  return { linework, dashedLinework };
}

function getSlabProfilesAndFilterForUtilityPlanContent(
  building: Building,
  levelId: string,
): {
  slabProfiles: PolyLine2[];
  filterSlabEdge?: (x1: number, y1: number, x2: number, y2: number) => boolean;
} {
  if (buildingIsEmpty(building)) {
    return { slabProfiles: [], filterSlabEdge: undefined };
  }

  const { foundationSuperSystems, settings } = building;
  const { planset } = settings;
  const { utilityDimensionType, excludeNonHouseSlabEdges } = planset;

  const slabProfiles: PolyLine2[] = [];
  const includeSlabProfileForDimensioning: PolyLine2[] = [];
  const ignoreSlabProfilesForDimensioning: PolyLine2[] = [];
  const mergeSlabProfilesForDimensioning: PolyLine2[] = [];
  const unionSlabProfilesForDimensioning: PolyLine2[] = [];

  function addToIgnoreOrMergeProfileForDimensioning(
    roomClass: RoomClass,
    planProfile: PolyLine2,
    includeSlabProfiles: PolyLine2[],
    _ignoreSlabProfiles: PolyLine2[],
    unionSlabProfiles: PolyLine2[],
  ) {
    switch (roomClass) {
      case RoomClass.HOUSE:
      case RoomClass.BASEMENT:
        includeSlabProfiles.push(polyLine2Copy(planProfile));
        break;
      case RoomClass.GARAGE:
      case RoomClass.PATIO:
      case RoomClass.DECK:
      case RoomClass.EXTERIOR:
      case RoomClass.PORCH:
        unionSlabProfiles.push(polyLine2Copy(planProfile));
        break;
      default:
        assertUnreachable(roomClass);
    }
  }

  for (const foundationId in foundationSuperSystems) {
    const foundationSuperSystem = foundationSuperSystems[foundationId];

    if (foundationSuperSystem?.levelId !== levelId) {
      continue;
    }

    const slabs = foundationSuperSystem.superSystem.slabs || [];

    for (let i = 0, l = slabs.length; i < l; i++) {
      const slab = slabs[i];
      switch (utilityDimensionType) {
        case UtilityDimensionType.OUTSIDE_EDGE_OF_FOUNDATION:
          excludeNonHouseSlabEdges
            ? addToIgnoreOrMergeProfileForDimensioning(
                slab.roomClass,
                slab.exteriorProfile[0],
                includeSlabProfileForDimensioning,
                ignoreSlabProfilesForDimensioning,
                mergeSlabProfilesForDimensioning,
              )
            : slabProfiles.push(polyLine2Copy(slab.exteriorProfile[0]));
          break;
        case UtilityDimensionType.OUTSIDE_EDGE_OF_SLAB:
          excludeNonHouseSlabEdges
            ? addToIgnoreOrMergeProfileForDimensioning(
                slab.roomClass,
                slab.planProfile[0],
                includeSlabProfileForDimensioning,
                ignoreSlabProfilesForDimensioning,
                mergeSlabProfilesForDimensioning,
              )
            : slabProfiles.push(polyLine2Copy(slab.planProfile[0]));
          break;
        default:
          assertUnreachable(utilityDimensionType);
      }
    }
  }

  function insertEdgePointsDueToOverlappingPolys(
    inputPolys: PolyLine2[],
    removePolys: PolyLine2[],
    outputPolys: PolyLine2[],
  ) {
    function collectInsertionPts(line: PolyLine2) {
      const arrayVec2: Vec2[] = [];
      const aaBBLine = aabb2ByPolyLine2s([line]);
      assertIsNotNull(aaBBLine);
      aabb2Grow(aaBBLine, GROW_POLYGON_EPSILON, GROW_POLYGON_EPSILON);

      for (let i = 0, l = removePolys.length; i < l; i++) {
        const aabbRemovePoly = aabb2ByPolyLine2s([removePolys[i]]);

        assertIsNotNull(aabbRemovePoly);
        if (!aabb2Overlaps(aabbRemovePoly, aaBBLine)) {
          continue;
        }

        for (let j = 0, k = polyLine2EdgeCount(removePolys[i]); j < k; j++) {
          const edge = polyLine2Edge(removePolys[i], j);

          const lineOverlapStatus = line2CollinearOverlap(
            line[0],
            line[1],
            line[2],
            line[3],
            edge[0],
            edge[1],
            edge[2],
            edge[3],
            false,
          );

          if (lineOverlapStatus === Line2CollinearOverlap.NONE || lineOverlapStatus === Line2CollinearOverlap.POINT) {
            continue;
          }

          if (lineOverlapStatus === Line2CollinearOverlap.LINE2_INSIDE_LINE1) {
            arrayVec2.push([edge[0], edge[1]]);
            arrayVec2.push([edge[2], edge[3]]);
          }

          if (lineOverlapStatus === Line2CollinearOverlap.PARTIAL) {
            if (vec2IsInEdgeBounds(edge[0], edge[1], line[0], line[1], line[2], line[3], false)) {
              arrayVec2.push([edge[0], edge[1]]);
            } else if (vec2IsInEdgeBounds(edge[2], edge[3], line[0], line[1], line[2], line[3], false)) {
              arrayVec2.push([edge[2], edge[3]]);
            }
          }
        }
      }
      return arrayVec2;
    }
    inputPolys.forEach((p) => {
      let newPoly: PolyLine2 | undefined = undefined;
      for (let i = 0, k = polyLine2EdgeCount(p); i < k; i++) {
        const line = polyLine2Edge(p, i);
        const insertionPts = collectInsertionPts(line);
        if (insertionPts.length === 0) {
          newPoly = newPoly ? polyLine2New([...newPoly, ...line]) : polyLine2New([...line]);
        } else {
          insertionPts.sort((a, b) => {
            const sqrdDista = vec2DistSquared(line[0], line[1], a[0], a[1]);
            const sqrdDistb = vec2DistSquared(line[0], line[1], b[0], b[1]);
            if (sqrdDista > sqrdDistb) {
              return 1;
            } else if (sqrdDista < sqrdDistb) {
              return -1;
            } else {
              return 0;
            }
          });
          for (let j = 0; j < insertionPts.length; j++) {
            newPoly = newPoly
              ? polyLine2New([...newPoly, insertionPts[j][0], insertionPts[j][1]])
              : polyLine2New([line[0], line[1], insertionPts[j][0], insertionPts[j][1]]);
          }
          assertIsNotUndefined(newPoly);
          newPoly = polyLine2New([...newPoly, line[2], line[3]]);
        }
      }
      if (newPoly) {
        outputPolys.push(polyLine2MergeCoincidentVertices(newPoly));
      }
    });
  }

  let filterSlabEdge: ((x1: number, y1: number, x2: number, y2: number) => boolean) | undefined = undefined;

  function overlapsAnyIgnoreSlabEdge(x1: number, y1: number, x2: number, y2: number) {
    for (let i = 0, l = ignoreSlabProfilesForDimensioning.length; i < l; i++) {
      for (let j = 0, k = polyLine2EdgeCount(ignoreSlabProfilesForDimensioning[i]); j < k; j++) {
        const edge = polyLine2Edge(ignoreSlabProfilesForDimensioning[i], j);

        const lineOverlapStatus = line2CollinearOverlap(x1, y1, x2, y2, edge[0], edge[1], edge[2], edge[3], false);

        if (lineOverlapStatus !== Line2CollinearOverlap.NONE && lineOverlapStatus !== Line2CollinearOverlap.POINT) {
          return true;
        }
      }
    }
    return false;
  }

  if (excludeNonHouseSlabEdges) {
    filterSlabEdge = overlapsAnyIgnoreSlabEdge;
    const polygonToUnion: PolyLine2Polygon[] = [];
    includeSlabProfileForDimensioning.forEach((p) => polygonToUnion.push([polyLine2SetCCW(p)]));
    mergeSlabProfilesForDimensioning.forEach((p) => polygonToUnion.push([polyLine2SetCCW(p)]));

    polygonUnion(polygonToUnion).forEach((p) =>
      unionSlabProfilesForDimensioning.push(...p.map((s) => polyLine2SetCCW(s))),
    );

    insertEdgePointsDueToOverlappingPolys(
      unionSlabProfilesForDimensioning,
      ignoreSlabProfilesForDimensioning,
      slabProfiles,
    );
  }

  return { slabProfiles, filterSlabEdge };
}

function foundationSlabSplitDimensionProfileAtSectionsGaps(
  foundationSubSystem: GeneratedFoundationSubSystem,
  slab: Slab,
  slabProfile: PolyLine2,
  sectionPredicate: (section: LinearFoundationSection) => boolean,
) {
  const { subSystem } = foundationSubSystem;
  const { strips } = subSystem;

  // break porch exterior edges that may have brick ledge door cuts (typically porticos)
  const { halfEdges, exteriorProfileHalfEdges } = slab;
  const effectiveHalfEdges = polyLine2DataReplaceIfLengthMismatch(
    slabProfile,
    exteriorProfileHalfEdges[0],
    defaultHalfEdgeData(foundationSubSystem.planarGraphId),
  );

  const splitPoints: ClosestPointToPolyLine2[] = [];
  for (const halfEdge of halfEdges) {
    const strip = strips[halfEdge] ?? strips[ahe(halfEdge)];
    if (!strip) {
      continue;
    }

    for (const section of strip.sections) {
      if (!isLinearFoundationSection(section)) {
        continue;
      }

      if (sectionPredicate(section)) {
        const pts: number[] = [];

        const { edge: leftEdge, gaps: leftGaps } = section.leftEdgeGaps;
        const { edge: rightEdge, gaps: rightGaps } = section.rightEdgeGaps;

        for (const gap of rightGaps) {
          const [ax, ay] = polyLine2EdgeEval(rightEdge, 0, gap.start);
          const [bx, by] = polyLine2EdgeEval(rightEdge, 0, gap.end);
          pts.push(ax, ay, bx, by);
        }

        for (const gap of leftGaps) {
          const [ax, ay] = polyLine2EdgeEval(leftEdge, 0, gap.start);
          const [bx, by] = polyLine2EdgeEval(leftEdge, 0, gap.end);
          pts.push(ax, ay, bx, by);
        }

        for (let j = 0; j < pts.length; j += 2) {
          const ax = pts[j];
          const ay = pts[j + 1];
          const res = closestPointPolyLine2(slabProfile, ax, ay);
          if (!res) {
            continue;
          }

          splitPoints.push(res);
        }
      }
    }
  }

  if (splitPoints.length > 0) {
    const slabProfileOut = splitPolyLineAtPoints(slabProfile, splitPoints);
    const slabProfileHalfEdges = edgeDataRemap(
      polyLine2OverlappingEdgeMapSlow(slabProfile, slabProfileOut),
      effectiveHalfEdges,
      arrayEdgeDataUnion,
      polyLine2EdgeCount(slabProfileOut),
      defaultHalfEdgeData(foundationSubSystem.planarGraphId),
    );

    return { slabProfile: slabProfileOut, slabProfileHalfEdges };
  }

  return { slabProfile, slabProfileHalfEdges: effectiveHalfEdges };
}

function foundationConditionPredicate(building: Pick<Building, "settings">) {
  const { planset: planSettings } = building.settings;
  return (cond: FoundationConditionTag) => {
    if (!planSettings.dimensionInteriorFootings) {
      return false;
    }
    return isSpanFoundationCondition[cond.condition];
  };
}

export function foundationPropPredicate(building: Pick<Building, "settings" | "levels">, levelId: string) {
  const { levels, settings } = building;
  const { planset: planSettings, foundation } = settings;
  const propCatalog = building.settings.props;
  const systemSettings = foundationSystemSettingsFromSettingsConfig(foundation);
  return (prop: Prop) => {
    if (!prop?.room) {
      return false;
    }

    if (slabTypeAtRoomClass(prop.room.attributes.roomClass, systemSettings) === SlabType.NONE) {
      return false;
    }
    if (levelIsBelow(prop.room.level.id, levelId, levels)) {
      return false;
    }
    // this is not a robust check and ultimately should be done by looking at the floors below
    if (levels[prop.room.level.id].floorIndex > 0) {
      return false;
    }

    const isPlumbingFoundationProp =
      planSettings.dimensionPlumbingProps && propCatalog[prop.src]?.isPlumbingArchitecturalProp;
    const isElectricalFoundationProp =
      planSettings.dimensionElectricalFloorProps && propCatalog[prop.src]?.isElectricalArchitecturalProp;
    return (
      // TODO: this check for "addToFoundationPlan" needs to be generalized to check for arbitrary tags.
      // user defined tags on props won't have any effect until this is removed.
      isPlumbingFoundationProp || isElectricalFoundationProp || entityContainsBuiltInLabels(prop, "addToFoundationPlan")
    );
  };
}

export function foundationUtilityPropPredicate(building: Building, levelId: string) {
  const { levels, settings } = building;
  const { planset: planSettings, foundation } = settings;
  const propCatalog = building.settings.props;
  const systemSettings = foundationSystemSettingsFromSettingsConfig(foundation);
  return (prop: Prop) => {
    if (!prop?.room) {
      return false;
    }

    if (slabTypeAtRoomClass(prop.room.attributes.roomClass, systemSettings) === SlabType.NONE) {
      return false;
    }
    if (levelIsBelow(prop.room.level.id, levelId, levels)) {
      return false;
    }
    // this is not a robust check and ultimately should be done by looking at the floors below
    if (levels[prop.room.level.id].floorIndex > 0) {
      return false;
    }

    const isPlumbingFoundationProp =
      planSettings.dimensionPlumbingProps && propCatalog[prop.src]?.isPlumbingArchitecturalProp;
    const isElectricalFoundationProp =
      planSettings.dimensionElectricalFloorProps && propCatalog[prop.src]?.isElectricalArchitecturalProp;
    const isUtilityProp = planSettings.dimensionPlumbingProps && propCatalog[prop.src]?.isUtilityArchitecturalProp;
    return (
      isPlumbingFoundationProp ||
      isElectricalFoundationProp ||
      isUtilityProp ||
      entityContainsBuiltInLabels(prop, "addToUtilityPlan")
    );
  };
}

export function foundationElectricalLoadPredicate(building: Pick<Building, "settings">, levelId: string) {
  const { settings } = building;
  const { planset: planSettings } = settings;
  return (load: LoadView) => {
    if (load.levelIdIndicator !== levelId) {
      return false;
    }
    const isElectricalFoundationProp =
      planSettings.dimensionElectricalFloorProps && load.type === LoadType.OUTLET_FLOOR;
    return isElectricalFoundationProp;
  };
}

export function foundationUtilityElectricalLoadPredicate(_: Building, levelId: string) {
  return (load: LoadView) => {
    if (load.levelIdIndicator !== levelId) {
      return false;
    }
    const isElectricalFoundationProp = load.type === LoadType.OUTLET_FLOOR;
    return isElectricalFoundationProp;
  };
}

function simplifyFoundationStrips(planarGraph: PlanarGraph, foundation: GeneratedFoundationSubSystem) {
  return mergeFoundationStrips(planarGraph, foundation.subSystem.strips);
}

function mergeFoundationStrips(planarGraph: PlanarGraph, paths: HalfEdgeToFoundationStrip) {
  const allStrips: HalfEdgeToFoundationStrip = {};
  for (const halfEdgeId in paths) {
    allStrips[halfEdgeId] = paths[halfEdgeId];
    allStrips[ahe(+halfEdgeId)] = paths[halfEdgeId];
  }

  function halfEdgeIsMergeable(halfEdgeIndexTest: number, halfEdgeIndex: number, mergeSpanConditions = true) {
    const condition = allStrips[halfEdgeIndex].path.condition;
    if (
      isPorchFoundationCondition[condition] ||
      isDeckFoundationCondition[condition] ||
      (isSpanFoundationCondition[condition] && mergeSpanConditions) ||
      isTransitionFoundationCondition[condition]
    ) {
      return true;
    }

    if (halfEdgeCollinear(halfEdgeIndex, halfEdgeIndexTest)) {
      return true;
    }

    return false;
  }

  function halfEdgesStripCompatible(halfEdgeIndexTest: number, halfEdgeIndex: number) {
    const strip = allStrips[halfEdgeIndex];
    const test = allStrips[halfEdgeIndexTest];

    // not valid
    if (!strip || !test) {
      return false;
    }

    // no sections on one of these strips
    if (!strip.sections.length || !test.sections.length) {
      return false;
    }

    // different foundation condition
    if (strip.path.condition !== test.path.condition) {
      return false;
    }

    // same condition but some section is different
    if (strip.sections.length !== test.sections.length) {
      return false;
    }

    const testAssembly = test.path.assembly;
    const stripAssembly = strip.path.assembly;

    if (
      testAssembly.assemblyType === FoundationAssemblyType.VOID ||
      stripAssembly.assemblyType === FoundationAssemblyType.VOID
    ) {
      return false;
    } else if (testAssembly.assemblyId !== stripAssembly.assemblyId) {
      return false;
    }

    return true;
  }

  function halfEdgeCollinear(halfEdgeIndexTest: number, halfEdgeIndex: number) {
    const [cwDirX, cwDirY] = planarGraphHalfEdgeTangent(planarGraph, halfEdgeIndexTest);
    const [dirX, dirY] = planarGraphHalfEdgeTangent(planarGraph, halfEdgeIndex);

    // collinear
    if (Math.abs(vec2Dot(cwDirX, cwDirY, dirX, dirY) - 1) < DIST_EPSILON) {
      return true;
    }

    return false;
  }

  function getNextHalfEdgeOnStrip(halfEdgeIndex: number): number | null {
    const adjHalfEdgeIndex = ahe(halfEdgeIndex);

    const hes = nextHalfEdgesCCW(planarGraph, adjHalfEdgeIndex, (he) => {
      return halfEdgesStripCompatible(he, halfEdgeIndex);
    });

    if (hes.length === 0) {
      return null;
    }

    const paths = nextHalfEdgePaths(planarGraph, adjHalfEdgeIndex);

    // this is a loop on the same condition, so collinearity does not matter
    if (paths.length === 1 && hes.length === 1) {
      if (halfEdgeIsMergeable(hes[0], halfEdgeIndex)) {
        return hes[0];
      }
    }

    // there are more than one option - we need to either find a collinear option or split the strip
    return hes.find((he) => halfEdgeIsMergeable(he, halfEdgeIndex, false)) ?? null;
  }

  function getPrevHalfEdgeOnStrip(halfEdgeIndex: number): number | null {
    const hes = nextHalfEdgesCCW(planarGraph, halfEdgeIndex, (he) => {
      return halfEdgesStripCompatible(ahe(he), halfEdgeIndex);
    });

    if (hes.length === 0) {
      return null;
    }

    const paths = nextHalfEdgePaths(planarGraph, halfEdgeIndex);

    //this is a loop on the same condition, so collinearity does not matter
    if (paths.length === 1 && hes.length === 1) {
      if (halfEdgeIsMergeable(ahe(hes[0]), halfEdgeIndex)) {
        return ahe(hes[0]);
      }
    }

    // there are more than one option - we need to either find a collinear option or split the strip
    const he = hes.find((he) => halfEdgeIsMergeable(ahe(he), halfEdgeIndex, false)) ?? null;
    if (he) {
      return ahe(he);
    }
    return null;
  }

  function nextHalfEdgesCCW(
    planarGraph: PlanarGraph,
    halfEdgeIndex: number,
    pred?: (halfEdgeIndex: number) => boolean,
  ): number[] {
    const startVertex = planarGraph.halfEdges[halfEdgeIndex * 2];

    const outgoingHalfEdges = planarGraph.vertexHalfEdges[startVertex];
    const outgoingHalfEdgeCount = outgoingHalfEdges.length;

    const halfEdgeIndexInVertexHalfEdgeArray = planarGraph.halfEdgeVertexIndex[halfEdgeIndex];

    const edges: number[] = [];

    for (let i = 1; i < outgoingHalfEdgeCount; i++) {
      const nxt = outgoingHalfEdges[(halfEdgeIndexInVertexHalfEdgeArray + i) % outgoingHalfEdgeCount];
      if (!pred || pred(nxt)) {
        edges.push(nxt);
      }
    }

    return edges;
  }

  function nextHalfEdgePaths(planarGraph: PlanarGraph, halfEdgeIndex: number): number[] {
    const startVertex = planarGraph.halfEdges[halfEdgeIndex * 2];

    const outgoingHalfEdges = planarGraph.vertexHalfEdges[startVertex];
    const outgoingHalfEdgeCount = outgoingHalfEdges.length;

    const halfEdgeIndexInVertexHalfEdgeArray = planarGraph.halfEdgeVertexIndex[halfEdgeIndex];

    const edges: number[] = [];

    for (let i = 1; i < outgoingHalfEdgeCount; i++) {
      const nxt = outgoingHalfEdges[(halfEdgeIndexInVertexHalfEdgeArray + i) % outgoingHalfEdgeCount];
      if (paths[nxt] || paths[ahe(nxt)]) {
        edges.push(nxt);
      }
    }

    return edges;
  }

  const strips: number[][] = [];
  let currentStrip: number[] = [];
  const visited: { [halfEdgeIndex: number]: boolean } = {};

  for (const id in paths) {
    if (visited[id]) {
      continue;
    }

    const startHalfEdgeIndex = paths[id].path.halfEdgeIndex;

    currentStrip = [];
    strips.push(currentStrip);

    // add collinear edges forward and apply to end of strip
    let currHalfEdgeIndex: number | null = startHalfEdgeIndex;

    while (currHalfEdgeIndex !== null && !visited[currHalfEdgeIndex]) {
      currentStrip.push(currHalfEdgeIndex);
      visited[currHalfEdgeIndex] = true;
      visited[ahe(currHalfEdgeIndex)] = true;
      currHalfEdgeIndex = getNextHalfEdgeOnStrip(currHalfEdgeIndex);
    }

    // collect collinear edges backward and apply to front of strip
    currHalfEdgeIndex = getPrevHalfEdgeOnStrip(startHalfEdgeIndex);

    while (currHalfEdgeIndex !== null && !visited[currHalfEdgeIndex]) {
      currentStrip.unshift(currHalfEdgeIndex);
      visited[currHalfEdgeIndex] = true;
      visited[ahe(currHalfEdgeIndex)] = true;
      currHalfEdgeIndex = getPrevHalfEdgeOnStrip(currHalfEdgeIndex);
    }
  }
  return strips;
}

const MIN_FOUNDATION_TAG_CONDITION_LENGTH = 36;

export function makeFoundationConditionTags(
  building: Pick<
    Building,
    | "foundations"
    | "planarGraphs"
    | "levels"
    | "foundationPaths"
    | "foundationHorizontalAssembliesPaths"
    | "foundationSuperSystems"
    | "floorPaths"
    | "foundationHorizontalAssembliesPaths"
  >,
  foundationAssemblies: Table<FoundationAssemblyEntity>,
  levelId: string,
): FoundationConditionTag[] {
  const { foundations, planarGraphs, levels, foundationPaths, floorPaths, foundationHorizontalAssembliesPaths } =
    building;
  const foundationConditionTags: FoundationConditionTag[] = [];

  for (const foundationId in foundations) {
    const foundation = foundations[foundationId];
    if (!foundation) {
      continue;
    }
    const rawPlanarGraph = planarGraphs[foundation.planarGraphId].planarGraph;

    if (!levelIsAboveOrEqual(foundation.levelId, levelId, levels)) {
      continue;
    }

    const { strips } = foundation.subSystem;
    const mergedPaths = simplifyFoundationStrips(rawPlanarGraph, foundation);
    const paths =
      foundationPaths[foundation.planarGraphId] ?? foundationHorizontalAssembliesPaths[foundation.planarGraphId];

    for (const mergedPath of mergedPaths) {
      // it doesn't matter which halfEdge we select
      // since they should all be identical
      const halfEdge = mergedPath[0];
      const path = paths[halfEdge] ?? paths[ahe(halfEdge)];
      const strip = strips[halfEdge] ?? strips[ahe(halfEdge)];

      if (!strip.sections.length) {
        continue;
      }

      const conditionStrips: FoundationStrip[] = [];

      // split any strips with sub conditions
      // this whole section should be updated with the integration of arbitrary footings
      // and sub-conditions on the foundation system.
      // right now this is pretty hacky
      const levelFloorPaths = floorPaths[path.planarGraphId];

      for (const he of mergedPath) {
        const strip = strips[he] ?? strips[ahe(he)];
        const { path } = strip;
        const floorPath = levelFloorPaths?.[he] ?? levelFloorPaths?.[ahe(he)];

        if (
          floorPath &&
          isGarageFoundationCondition[path.condition] &&
          floorPath.doors.find(isGarageDoorOperationType)
        ) {
          const nPath = { ...path, condition: FoundationCondition.GARAGE_DOOR_EXTERIOR };
          const nStrip = { ...strip, path: nPath };
          foundationConditionTags.push(
            makeFoundationConditionTag({
              foundation,
              strips: [nStrip],
              condition: nPath.condition,
              foundationAssemblies,
              planarGraphs,
            }),
          );
        } else {
          conditionStrips.push(strip);
        }
      }

      if (conditionStrips.length === 0) {
        continue;
      }

      foundationConditionTags.push(
        makeFoundationConditionTag({
          foundation,
          strips: conditionStrips,
          condition: strip.path.condition,
          foundationAssemblies,
          planarGraphs,
        }),
      );
    }
  }
  const filteredFoundationConditionTags = foundationConditionTags.filter((x) => {
    if (x.data.drawingId === DrawingId.NONE) {
      return false;
    }
    if (!x.strips.length) {
      return false;
    }
    const firstStrip = x.strips[0];
    const foundationId = Object.keys(foundations).find((id) => {
      const f = foundations[id];
      return f && f.subSystem.strips[firstStrip.path.halfEdgeIndex];
    });
    if (!foundationId) {
      return false;
    }
    const foundation = foundations[foundationId];
    if (!foundation) {
      return false;
    }
    const joinedPolyLine = foundationStripConditionLine(x.strips, planarGraphs);
    const length = polyLine2Length(joinedPolyLine);

    return length >= MIN_FOUNDATION_TAG_CONDITION_LENGTH;
  });
  return filteredFoundationConditionTags;
}

function makeFoundationConditionTag({
  foundation,
  strips,
  condition,
  foundationAssemblies,
  planarGraphs,
}: {
  foundation: GeneratedFoundationSubSystem;
  strips: FoundationStrip[];
  condition: FoundationCondition;
  foundationAssemblies: Table<FoundationAssemblyEntity>;
  planarGraphs: Table<PolygonPlanarGraph>;
}): FoundationConditionTag {
  const { settings } = foundation;
  const { foundationSuperSystemType, foundationSubSystemType, foundationConditions } = settings;

  const lines = foundationStripConditionLines(strips, planarGraphs);
  lines.sort((a, b) => polyLine2Length(b) - polyLine2Length(a));

  // initial starting position of the tag is the longest strip on the condition
  const [startX, startY, endX, endY] = lines[0];
  const [cx, cy] = vec2Lerp(startX, startY, endX, endY, 0.5);
  const [nx, ny] = line2Normal(startX, startY, endX, endY);

  // we can use any strip here since they are identical
  const offset = 0; // foundationStripWidth(strips[0]) / 2;

  const rotation = vec2AngleBetweenCW(nx, ny, 0, -1);
  const direction = TagDirection.LEFT;

  const settingData = foundationConditions[condition];
  const defaultData = defaultFoundationConditionDataNew(
    foundationSuperSystemType,
    foundationSubSystemType,
    condition,
    settings.foundationAssembly,
  );

  // settings data is partial,
  // so we need to fill in missing info.
  const data = {
    ...defaultData,
    ...settingData,
  };

  let label: string | undefined;
  const assembly = strips[0]?.path.assembly;
  if (assembly.assemblyType !== FoundationAssemblyType.VOID) {
    if (assembly.assemblyId !== data.assemblyId) {
      label = foundationAssemblies[assembly.assemblyId].tag;
    }
    if (assembly.drawingId !== undefined) {
      data.drawingId = assembly.drawingId;
    }
    if (assembly.drawingNumber !== undefined) {
      data.drawingNumber = assembly.drawingNumber;
    }
  }

  return foundationConditionTagNew({
    strips,
    condition,
    data,
    x: cx + nx * offset,
    y: cy + ny * offset,
    direction,
    rotation,
    lines,
    label,
  });
}

function foundationConditionTagNew({
  strips,
  condition,
  direction,
  data,
  x,
  y,
  lines,
  rotation,
  label,
}: {
  strips: FoundationStrip[];
  condition: FoundationCondition;
  direction: TagDirection;
  data: FoundationConditionData;
  x: number;
  y: number;
  lines: PolyLine2[];
  rotation: number;
  label?: string;
}): FoundationConditionTag {
  return {
    strips,
    condition,
    direction,
    data,
    x,
    y,
    lines,
    rotation,
    label,
  };
}

export function tagDirectionOpposite(dir: TagDirection) {
  return dir === TagDirection.LEFT ? TagDirection.RIGHT : TagDirection.LEFT;
}

export function foundationTagOffset(rotation: number, conditionLength: number): Vec2 {
  const dx = Math.cos(Math.PI / 2 + rotation);
  const dy = Math.sin(Math.PI / 2 + rotation);
  const dl = conditionLength / Math.sqrt(2);
  const tx = dx * dl;
  const ty = dy * dl;
  return [-tx, -ty];
}

function foundationStripConditionLines(strips: FoundationStrip[], planarGraphs: Table<PolygonPlanarGraph>) {
  const stripLines: PolyLine2[] = [];
  let stripLine: PolyLine2 | undefined;
  let stripOffsets: number[] = [];
  for (const strip of strips) {
    const path = strip.path;
    const offset = path.subSystemOffset;
    const [startX, startY, endX, endY] = foundationPathCoordinates(path, planarGraphs);
    const pl = polyLine2ByTwoPoints(startX, startY, endX, endY);
    if (!stripLine) {
      stripLine = pl;
      stripOffsets.push(offset);
      continue;
    }
    const isCollinear = line2Collinear(
      stripLine[0],
      stripLine[1],
      stripLine[2],
      stripLine[3],
      pl[0],
      pl[1],
      pl[2],
      pl[3],
    );
    if (isCollinear) {
      stripLine = polyLine2MergeCoincidentVertices(polyLine2New([...stripLine, ...pl]));
      stripOffsets.push(offset);
    } else {
      const finalStrip = offsetPolyLine2VariableStep(polyLine2New(stripLine), stripOffsets).pl;
      stripLines.push(finalStrip);
      stripLine = pl;
      stripOffsets = [offset];
    }
  }

  if (!!stripLine) {
    const finalStrip = offsetPolyLine2VariableStep(polyLine2New(stripLine), stripOffsets).pl;
    stripLines.push(finalStrip);
  }

  const lines = joinPolyLineCollinearSequences(stripLines).map((p) => polyLine2Simplify(p));
  return lines;
}

function foundationStripConditionLine(strips: FoundationStrip[], planarGraphs: Table<PolygonPlanarGraph>) {
  return joinPolyLine2Sequence(foundationStripConditionLines(strips, planarGraphs));
}

export function foundationTagLeaderEnd(tag: FoundationConditionTag): Vec2 {
  const { x: sx, y: sy } = tag;
  const { x: ex, y: ey } = closestPointPolyLine2(tag.lines[0], sx, sy, false);

  let [odx, ody] = foundationTagOffset(tag.rotation, 1);
  [odx, ody] = vec2Normalize(odx, ody);

  const leader = polyLine2ByTwoPoints(sx, sy, ex, ey);
  polyLine2ExtendEnd(leader, foundationStripWidth(tag.strips[0]));

  return [leader[2], leader[3]];
}

export function foundationConditionKey(conditionTags: FoundationConditionTag[]) {
  const conditionData: FoundationConditionTag[] = [];
  const conditionDetailIndex: { [detailId: string]: { [detailNumber: number]: true } } = {};

  for (const conditionTag of conditionTags) {
    const {
      data: { drawingId, drawingNumber },
    } = conditionTag;
    if (conditionDetailIndex[drawingId] === undefined) {
      conditionDetailIndex[drawingId] = {};
    }
    if (conditionDetailIndex[drawingId][drawingNumber] === undefined) {
      conditionDetailIndex[drawingId][drawingNumber] = true;
      conditionData.push(conditionTag);
    }
  }

  conditionData.sort((a, b) => a.data.drawingNumber - b.data.drawingNumber);

  return conditionData;
}

function foundationStripWidth(strip: FoundationStrip) {
  const { sections } = strip;

  let width = 20;

  for (const section of sections) {
    const { size } = section;
    const { left, right } = size;

    const sectionWidth = left + right;

    if (width < sectionWidth) {
      width = sectionWidth;
    }
  }

  return width;
}

function hydrateFoundationDimensionDocEntityHandles(
  dims: DimensionRowOutput[],
  building: Pick<Building, "mirror" | "planarGraphs" | "roomsByLevel" | "settings" | "rooms">,
  levelId: string,
  config: {
    useInteriorOrientation: boolean;
    dimensionNew: (props: DimensionRowOutput & { docEntityHandle: DocEntityHandle }) => GeneratedDimension;
    roomHandleNew: (
      criteria: ProvenanceCriterion[],
      roomEdges: LevelRoomEdge[],
      rowLevel: DimensionRowLevel,
      isOrthogonal: boolean,
    ) => DocEntityHandle;
    orientationHandleNew: (
      criteria: ProvenanceCriterion[],
      levelId: string,
      orientation: EdgeOrientation,
      rowLevel: DimensionRowLevel,
    ) => DocEntityHandle;
  },
): GeneratedDimension[] {
  const criteria = makeProvenanceCriteria(building.settings, AutoFoundationExteriorDimensionProvenanceCriteria);

  return dims.map((dim) => {
    const { halfEdges, dimLine, rowLevel } = dim;
    const { halfEdgeData, fallbackLevelId } = collectDefinedHalfEdges(halfEdges);
    const dirMatch = getMatchingEdgeOrientation(dimLine, building.mirror, config.useInteriorOrientation);
    const isOrthogonal = dirMatch !== undefined;

    if (halfEdgeData?.length && rowLevel !== undefined) {
      const roomEdges = getRoomEdgesFromHalfEdgeData(
        halfEdgeData,
        building.planarGraphs,
        building.roomsByLevel,
        building?.rooms ?? {},
        false,
      );

      if (roomEdges.length) {
        return config.dimensionNew({
          ...dim,
          docEntityHandle: config.roomHandleNew(criteria, roomEdges, rowLevel, isOrthogonal),
        });
      }
    }

    if (dirMatch === undefined || rowLevel === undefined) {
      return config.dimensionNew({
        ...dim,
        docEntityHandle: { type: "LEVEL", id: fallbackLevelId ?? levelId },
      });
    }

    return config.dimensionNew({
      ...dim,
      docEntityHandle: config.orientationHandleNew(criteria, fallbackLevelId ?? levelId, dirMatch, rowLevel),
    });
  });
}

function generatedFoundationExteriorDimensionHydrateDocEntityHandle(
  dims: DimensionRowOutput[],
  building: Pick<Building, "mirror" | "planarGraphs" | "roomsByLevel" | "settings" | "rooms">,
  levelId: string,
): GeneratedDimension[] {
  return hydrateFoundationDimensionDocEntityHandles(dims, building, levelId, {
    useInteriorOrientation: false,
    dimensionNew: generatedFoundationExteriorDimensionNew,
    roomHandleNew: autoDimensionFoundationExteriorByRoomHandleMultipleNew,
    orientationHandleNew: autoDimensionFoundationExteriorByOrientationHandleNew,
  });
}

function generatedFoundationInteriorDimensionHydrateDocEntityHandle(
  dims: DimensionRowOutput[],
  building: Pick<Building, "mirror" | "planarGraphs" | "roomsByLevel" | "settings" | "rooms">,
  levelId: string,
): GeneratedDimension[] {
  return hydrateFoundationDimensionDocEntityHandles(dims, building, levelId, {
    useInteriorOrientation: true,
    dimensionNew: generatedFoundationInteriorDimensionNew,
    roomHandleNew: autoDimensionFoundationInteriorByRoomHandleMultipleNew,
    orientationHandleNew: autoDimensionFoundationInteriorByOrientationHandleNew,
  });
}
